import { MoreOutlined, StarFilled, StarOutlined } from "@ant-design/icons"
import { Button, Dropdown, Flex, Table, Tag, type TableProps, Typography } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import React from "react"
import { useTranslation } from "react-i18next"

import { TABLE_SURFACES } from "../../../components/EntityTable/surfaces.ts"
import { ResizableHeaderCell } from "../../../components/Table/ResizableHeaderCell.tsx"
import { useColumnResize } from "../../../hooks/use-column-resize.tsx"
import { useTableColumnConfig } from "../../../hooks/use-table-column-config.tsx"
import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import { getFileIcon } from "../../Project/ProjectViewPage/helpers/file.tsx"
import { documentTypeTagColor } from "../helpers/document-type-tag-color.ts"
import { type FileItem } from "../types/file-item.types.ts"

const { Text } = Typography

interface FilesListViewProps {
  items: FileItem[]
  actions: (file: FileItem) => MenuItemType[]
  /** Omit both together for a view with no bulk actions behind it (Trash) —
   *  a selection checkbox nothing acts on would be exactly the kind of fake
   *  control the Files feature explicitly avoids. */
  selectedKeys?: Set<string>
  onToggleSelect?: (file: FileItem) => void
  onPreview: (file: FileItem) => void
  isFavorite?: (file: FileItem) => boolean
  onToggleFavorite?: (file: FileItem) => void
  /** Trash: swaps the Modified column for Deleted by/Deleted — same table,
   *  same Actions-column mechanism (the caller's own `actions(file)`
   *  supplies Restore/Delete permanently instead of the normal menu), not a
   *  second File Table implementation. */
  trashMode?: boolean
}

const EMPTY_CELL = "—"

export function FilesListView({
  items,
  actions,
  selectedKeys,
  onToggleSelect,
  onPreview,
  isFavorite,
  onToggleFavorite,
  trashMode = false
}: FilesListViewProps) {
  const { t } = useTranslation()

  // All Files is the master inventory of every uploaded file, most of which
  // are never classified — a Document Type/Status/Version column that reads
  // "Not assigned" on most rows reads as broken, not as a feature. The one
  // thing worth surfacing here is *whether* a file has been turned into a
  // Document, as a compact tag beside its name; the full picture (Status,
  // Version, Related To) lives in File Details, and assigning/changing the
  // type is a menu action (`files.actions.assignDocumentType`), not an
  // always-visible picker.
  const trailingColumns: NonNullable<TableProps<FileItem>["columns"]> = trashMode
    ? [
        {
          title: t("files.trash.columns.deletedBy"),
          key: "deletedBy",
          render: (_, file) => file.deletedByName || EMPTY_CELL
        },
        {
          title: t("files.trash.columns.deleted"),
          key: "deleted",
          render: (_, file) => getFormatDate(file.deletedAt) ?? EMPTY_CELL
        }
      ]
    : [
        {
          title: t("files.columns.modified"),
          key: "modified",
          render: (_, file) => getFormatDate(file.updated) ?? EMPTY_CELL
        }
      ]

  const fullColumns: TableProps<FileItem>["columns"] = [
    {
      title: t("files.columns.name"),
      key: "name",
      align: "left",
      render: (_, file) => (
        <Flex align="center" gap="small">
          {onToggleFavorite && (
            <Button
              type="text"
              size="small"
              aria-label={t("files.actions.favorite", { name: file.name })}
              icon={
                isFavorite?.(file) ? (
                  <StarFilled className="text-warning" />
                ) : (
                  <StarOutlined />
                )
              }
              onClick={() => onToggleFavorite(file)}
            />
          )}
          <span className="[&_svg]:text-base">{getFileIcon(file.mimeType)}</span>
          <a onClick={() => onPreview(file)}>
            <Text ellipsis={{ tooltip: file.name }}>{file.name}</Text>
          </a>
          {file.documentType && (
            <Tag color={documentTypeTagColor(file.documentType)} className="!m-0 shrink-0">
              {file.documentTypeLabel}
            </Tag>
          )}
          {file.customerUploaded && (
            <Tag color="blue" className="!m-0 shrink-0">
              {file.createdByName
                ? t("files.filters.sharedByCustomerNamed", { name: file.createdByName })
                : t("files.filters.sharedByCustomer")}
            </Tag>
          )}
        </Flex>
      )
    },
    {
      title: t("files.columns.type"),
      key: "type",
      render: (_, file) =>
        file.fileTypeLabel ??
        (file.source === "album-attachment"
          ? t("files.columns.typeFile")
          : t("files.columns.typeDocument"))
    },
    {
      title: t("files.columns.size"),
      key: "size",
      render: (_, file) => file.formattedFileSize || EMPTY_CELL
    },
    ...trailingColumns,
    {
      title: t("files.columns.actions"),
      key: "actions",
      width: 80,
      render: (_, file) => (
        <Dropdown trigger={["click"]} menu={{ items: actions(file) }}>
          <Button
            type="text"
            size="small"
            icon={<MoreOutlined />}
            aria-label={t("files.actions.menu", { name: file.name })}
          />
        </Dropdown>
      )
    }
  ]

  const { columns, fullConfig, setColumns } = useTableColumnConfig(
    TABLE_SURFACES.files,
    trashMode ? "trash" : "default",
    fullColumns
  )
  const { tableWrapRef, decorateColumns } = useColumnResize<FileItem>({
    fullConfig,
    setColumns
  })

  return (
    <div ref={tableWrapRef}>
      <Table<FileItem>
        rowKey="key"
        size="small"
        columns={decorateColumns(columns)}
        components={{ header: { cell: ResizableHeaderCell } }}
        dataSource={items}
        pagination={false}
        rowSelection={
          selectedKeys && onToggleSelect
            ? {
                selectedRowKeys: [...selectedKeys],
                onSelect: (file) => onToggleSelect(file)
              }
            : undefined
        }
      />
    </div>
  )
}
